import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useChatGptApiRequest } from '@sections/chat-gpt/utils/useChatGptApiRequest';
import { GPTSelect } from '@components/GPTSelect';
import { FormGroup, StyledButton, StyledLabel } from './Form.styles';
import { FieldName, FormBody, FormDataFieldState, FormType, PromptType, WriteMySubfieldsState } from './Form.types';
import { GPTInput } from '@components/GPTInput/GPTInput';
import { addAction } from '@utils/logger';

import { useTranslation } from 'react-i18next';

const analytics = require('../../../../static/js/joy');

interface FormProps {
  handleOnSubmit: (data: { loading: boolean; status: number; response: string }) => void;
}

export const Form: React.FC<FormProps> = ({ handleOnSubmit }) => {
  const { fetchData } = useChatGptApiRequest();
  const [isLoading, setIsLoading] = useState(false);
  const [hasChangedInput, setHasChangedInput] = useState(true);

  const { t } = useTranslation('chatGPT');

  const { formOptions, writeMyOptions } = useMemo(() => {
    const formOptions = t('form.formOptions', {
      FieldName,
      FormType,
      PromptType,
      interpolation: { escapeValue: false },
    }) as unknown as FormBody[];

    const writeMyOptions = formOptions.map((fields) => ({
      label: fields.displayText,
      value: fields.name,
    }));

    return { formOptions, writeMyOptions };
  }, [t]);

  const [writeMyState, setWriteMy] = useState<FormDataFieldState>({ value: writeMyOptions[0], index: 0 });

  const writeMy = useMemo(() => formOptions[writeMyState.index], [formOptions, writeMyState.index]);

  const [writeMySubfieldsState, setWriteMySubfieldsState] = useState<WriteMySubfieldsState[]>([]);

  useEffect(() => {
    if (writeMy.fields) {
      const initialSubFieldState = writeMy.fields.map((subfield) => {
        return subfield.type === FormType.SELECT
          ? { name: subfield.name, label: subfield.values[0].label, value: subfield.values[0].value }
          : { name: subfield.name, label: '', value: '' };
      });
      setWriteMySubfieldsState(initialSubFieldState);
    }
  }, [writeMy.fields]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>, fieldIndex) => {
      if (writeMySubfieldsState) {
        const newSubFieldState = [...writeMySubfieldsState];
        newSubFieldState[fieldIndex] = {
          name: formOptions[writeMyState.index].fields[fieldIndex].name,
          label: e.target.value,
          value: e.target.value,
        };
        setWriteMySubfieldsState(newSubFieldState);
      }
    },
    [formOptions, writeMyState.index, writeMySubfieldsState]
  );

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setIsLoading(true);

    const writeMyValue = writeMyState.value.label;
    const styleOfIndex = formOptions[writeMyState.index].fields.findIndex((field, index) => {
      return field.label === t('form.inStyleOfOptionsLabel');
    });
    const styleOfValue = writeMySubfieldsState[styleOfIndex]?.label;

    handleOnSubmit({ loading: true, status: 200, response: '' });

    const payloadStrings = writeMySubfieldsState.map(({ name, value }) => ({ key: name, value }));

    analytics.segment.sendTelemetryEvent('ChatGPTApiRequest', { label: writeMy.name, extraInfo: { payloadStrings } });
    addAction('ChatGPTApiRequest', {
      writeMy: writeMyValue,
      styleOf: styleOfValue,
      promptType: writeMy.name,
      payloadStrings: payloadStrings,
    });

    const startTime = Date.now();
    let status = 200;
    let error: string;
    let responseData: string;
    fetchData({ promptType: writeMy.name, payloadStrings })
      .then((response) => {
        responseData = response.textCompletionV2.trim().replace(/(?:\r\n|\r|\n)/g, '</br>');
      })
      .catch((e) => {
        status = 500;
        error = e?.message;
        responseData = t('form.defaultError');
      })
      .finally(() => {
        analytics.segment.sendTelemetryEvent('ChatGPTApiResponse', {
          status,
          responseLength: responseData ? responseData?.length : 0,
        });
        addAction('ChatGPTApiResponse', {
          status,
          promptType: writeMy.name,
          payloadStrings: payloadStrings,
          writeMy: writeMyValue,
          styleOf: styleOfValue,
          length: responseData ? responseData?.length : 0,
          duration: Date.now() - startTime,
          error,
        });

        handleOnSubmit({
          loading: false,
          status,
          response: error || !responseData ? t('form.defaultError') : responseData,
        });

        setHasChangedInput(false);
        setIsLoading(false);
      });
  };

  const weddingWritersUnblockerData = t('sections.weddingWritersUnblocker');

  return (
    <form onSubmit={onSubmit}>
      <FormGroup marginBottom={4} marginX="auto">
        <StyledLabel>{t('form.helpMeWithSelectLabel')}&#8230;</StyledLabel>
        <GPTSelect
          options={writeMyOptions}
          onClickedOption={(selectedOption, index) => {
            setHasChangedInput(true);
            setWriteMy({ value: selectedOption, index });
          }}
          preSelectedOption={writeMyState.value}
        />
      </FormGroup>

      {writeMySubfieldsState &&
        writeMy.fields?.map((field, fieldIndex) => (
          <FormGroup marginBottom={4} marginX="auto" key={`write-my-${fieldIndex}`}>
            <StyledLabel>{field.label}&#8230;</StyledLabel>
            {field.type === FormType.SELECT && (
              <GPTSelect
                options={field.values}
                onClickedOption={(selectedOption) => {
                  const newSubFieldState = [...writeMySubfieldsState];
                  newSubFieldState[fieldIndex] = { ...selectedOption, name: field.name };
                  setHasChangedInput(true);
                  setWriteMySubfieldsState(newSubFieldState);
                }}
                preSelectedOption={
                  writeMySubfieldsState[fieldIndex]
                    ? writeMySubfieldsState[fieldIndex]
                    : {
                        label: '',
                        value: '',
                      }
                }
              />
            )}
            {field.type === FormType.INPUT && (
              <GPTInput
                key={writeMyState.index + fieldIndex}
                onChange={(e) => handleInputChange(e, fieldIndex)}
                value={writeMySubfieldsState[fieldIndex] ? writeMySubfieldsState[fieldIndex].label : ''}
                placeholders={field.placeholders}
                maxLength={180}
                required
              />
            )}
          </FormGroup>
        ))}
      <FormGroup marginTop={8} marginX="auto">
        <StyledButton disabled={isLoading} id="ChatGPTSubmit" variant="primary" type="submit" skipRedirectWithTelemetry>
          {weddingWritersUnblockerData.submitCTA} {hasChangedInput ? '' : weddingWritersUnblockerData.submitCTAAgain}
        </StyledButton>
      </FormGroup>
    </form>
  );
};
